import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { API } from "../../lib/api";
import { PageHeader, Loading, Table, Empty, StatusBadge } from "../../components/common";
import { money, fmtDate } from "../../lib/format";

const REPORTS = [
  ["liability-by-month", "Tax Liability by Month"],
  ["sales-by-month", "Sales by Month"],
  ["taxable-vs-nontaxable", "Taxable vs Non-Taxable Sales"],
  ["tax-payments", "Tax Payments"],
  ["past-due", "Past-Due Balances"],
  ["vault-funding", "Tax Vault Funding"],
];

const MONEY_COLS = { "liability-by-month": [1, 2, 3, 4, 5, 6], "sales-by-month": [1, 2, 3, 4, 5, 6],
  "taxable-vs-nontaxable": [1, 2, 3, 4, 5, 6], "tax-payments": [3], "past-due": [1, 2, 3, 4, 5], "vault-funding": [2] };

export default function Reports() {
  const [type, setType] = useState("liability-by-month");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    API.get(`/reports/${type}`).then((r) => setData(r.data)).catch(() => setData({ headers: [], rows: [], chart: [] }));
  }, [type]);

  const moneyCols = MONEY_COLS[type] || [];

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" sub="Real numbers from your database — exportable to CSV."
        right={<a className="btn-outline-gov" href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/${type}?format=csv`} data-testid="report-export">
          <Download className="h-4 w-4" /> Export CSV</a>} />

      <div className="mb-5 flex flex-wrap gap-2" data-testid="report-selector">
        {REPORTS.map(([v, l]) => (
          <button key={v} onClick={() => setType(v)} data-testid={`report-${v}`}
            className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${type === v ? "border-navy bg-navy text-white" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>{l}</button>
        ))}
      </div>

      {!data ? <Loading label="Building report" /> : (
        <>
          {data.chart.length > 0 && ["liability-by-month", "sales-by-month", "taxable-vs-nontaxable"].includes(type) && (
            <div className="card-gov mb-6 p-5">
              <div className="h-72" data-testid="report-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.chart.map((c) => ({ name: c.label, Gross: c.gross / 100, Taxable: c.taxable / 100, Liability: c.liability / 100, Paid: c.paid / 100 }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <Legend />
                    <Bar dataKey="Gross" fill="#1B3A5C" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Taxable" fill="#D4AF37" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Liability" fill="#B23B23" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Paid" fill="#059669" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {data.rows.length === 0 ? <Empty title="No data for this report" /> : (
            <Table headers={data.headers} testid="report-table">
              {data.rows.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  {row.map((cell, j) => (
                    <td key={j} className={`px-4 py-2.5 ${moneyCols.includes(j) ? "money font-semibold" : ""}`}>
                      {moneyCols.includes(j) ? money(cell, { cents: true })
                        : typeof cell === "string" && /^[A-Z_]{3,}$/.test(cell) ? <StatusBadge status={cell} />
                        : cell}
                    </td>
                  ))}
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
    </div>
  );
}
